import React, { useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@/lib/auth-context';
import { creditsToUsd, formatCreditsLabel, formatUsd } from '@/utils/credits';
import { normalizeImg } from '@/lib/images';
import { getMarketplaceCatalog, getOwnedItems, purchaseItem } from '@/lib/coreApi';

type MarketItem = {
  id: string;
  sku: string;
  title: string;
  description?: string;
  previewUrl?: string;
  meta?: Record<string, unknown>;
  priceCredits: number | null;
  priceCents?: number | null;
  kind: string;
  owned?: boolean;
};

export default function ProspectMarketplace() {
  const { session } = useAuthContext() as { session: { access_token?: string } | null };
  const accessToken = session?.access_token;

  const [balance, setBalance] = useState<number>(0);
  const [items, setItems] = useState<MarketItem[]>([]);
  const [ownedIds, setOwnedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [spending, setSpending] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const balanceUsd = useMemo(() => creditsToUsd(balance), [balance]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [catalogRes, ownedRes] = await Promise.all([
          getMarketplaceCatalog('prospect'),
          getOwnedItems(),
        ]);

        if (cancelled) return;

        const sectionItems: MarketItem[] = [];
        const sections = catalogRes?.data?.sections || {};
        Object.values(sections).forEach((list) => {
          if (Array.isArray(list)) {
            sectionItems.push(...list);
          }
        });
        setItems(sectionItems);
        setOwnedIds(new Set(
          (Array.isArray(ownedRes?.data) ? ownedRes.data : []).map((row: { item_id: string }) => row.item_id),
        ));
      } catch (e) {
        console.error('[ProspectMarketplace] load failed', e);
        if (!cancelled) setError('Could not load marketplace.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  async function handleBuy(item: MarketItem) {
    setSpending(item.id);
    setError(null);
    setMessage(null);

    try {
      const result = await purchaseItem({ item_id: item.id, quantity: 1 });

      setMessage(`Unlocked: ${item.title}`);
      setOwnedIds((prev) => new Set([...prev, item.id]));

      if (typeof result?.data?.new_balance === 'number') {
        setBalance(result.data.new_balance);
      }
    } catch (e: unknown) {
      console.error('[ProspectMarketplace] buy failed', e);
      setError((e as Error).message || 'Could not complete purchase.');
    } finally {
      setSpending(null);
    }
  }

  const cosmetics = useMemo(
    () =>
      items.filter((i) =>
        ['badge', 'avatar', 'theme', 'border', 'digital_good'].includes(i.kind)
      ),
    [items]
  );

  const aiTools = useMemo(
    () => items.filter((i) => i.kind === 'ai_tool'),
    [items]
  );

  const specialOffers = useMemo(
    () => items.filter((i) => i.kind === 'special_offer'),
    [items]
  );

  function ItemCard({ item }: { item: MarketItem }) {
    const price = item.priceCredits ?? 0;
    const isOwned = ownedIds.has(item.id);
    const disabled = isOwned || spending === item.id;

    return (
      <div className="flex h-full flex-col justify-between rounded-2xl bg-white/80 p-4 shadow-soft">
        <div className="space-y-3">
          {item.previewUrl ? (
            <img
              src={normalizeImg(item.previewUrl)}
              alt={item.title}
              className="h-16 w-16 rounded-xl border border-shell/70 object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-shell/70 text-xs text-cocoa/70">
              {item.kind}
            </div>
          )}
          <div>
            <div className="text-sm font-semibold text-cocoa">{item.title}</div>
            {item.description && (
              <p className="text-xs text-cocoa/70 line-clamp-2">{item.description}</p>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-cocoa">
            {formatCreditsLabel(price)}
            <span className="ml-1 text-xs text-cocoa/60">(~{formatUsd(creditsToUsd(price))})</span>
          </span>
          <button
            disabled={disabled}
            onClick={() => handleBuy(item)}
            className="rounded-full bg-terracotta px-3 py-1.5 text-xs font-semibold text-white shadow disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isOwned ? 'Owned' : spending === item.id ? 'Buying...' : 'Buy'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-cocoa">Prospect Marketplace</h1>
        <p className="text-sm text-cocoa/70">
          Customize your profile with badges, avatars, and digital goods.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/80 p-4 shadow-soft">
        <div className="text-sm text-cocoa/70">Your balance</div>
        <div className="text-xl font-semibold text-cocoa">
          {loading ? '—' : (
            <>
              {formatCreditsLabel(balance)}
              <span className="block text-sm font-normal text-cocoa/60">~ {formatUsd(balanceUsd)}</span>
            </>
          )}
        </div>
      </div>

      {message && (
        <div className="rounded-xl border border-mint/40 bg-mint/10 px-4 py-3 text-sm text-cocoa/80">
          {message}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-brick/40 bg-brick/10 px-4 py-3 text-sm text-brick">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-cocoa/70">Loading marketplace...</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand bg-white/70 p-6 text-center text-sm text-cocoa/70">
          No marketplace items are available right now.
        </div>
      ) : (
        <div className="space-y-8">
          {cosmetics.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg font-semibold text-cocoa">Cosmetics</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {cosmetics.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          )}

          {aiTools.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg font-semibold text-cocoa">AI Tools</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {aiTools.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          )}

          {specialOffers.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg font-semibold text-cocoa">Special Offers</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {specialOffers.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
